/**
 * LogActivityHost.tsx — the ONE Log Activity sheet for the sales pages (Log
 * Activity slice C1, RULED 2026-10-08, R1). Dashboard mounts it around the
 * sales pages only. It owns the single sheet instance and the create handler
 * — create, then the complete that follows a logged call / email / meeting /
 * note, the toasts and the error behaviour exactly as slice B had them on the
 * Activities page — and exposes open(prefill, opener) plus a "created"
 * subscription. No window event, and NO key handler (R2: the L shortcut is
 * slice C2): the only listeners are the sheet's own, while it is open.
 *
 * Focus (R3, R5): the opener names WHERE focus goes when the sheet closes,
 * resolved at close time — the contact's row after the drawer closed, the
 * card's kebab after its menu item unmounted. When the resolved element is
 * gone, focus falls back to the page's main region (made focusable for that
 * one call), never to whatever the browser picks.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import type { SalesContact } from '../../types'
import type { SalesActivity as WireActivity } from '../../../../api/types'
import { fetchContacts, createActivity, completeActivity } from '../../api'
import { apiErrorMessage } from '../../../../api/client'
import type { CreateActivityBody } from './activityLog'
import { LogActivitySheet, MARK_DONE_FAILED } from './LogActivitySheet'
import { LogActivityContext, type LogActivityApi, type LogActivityPrefill, type FocusReturn } from './logActivityShared'

/** The contacts read's own cap — the picker's list AND the Activities rows'
 *  name lookup read the same page (OwnerSelect's DIRECTORY_CAP shape). */
export const CONTACTS_CAP = 200

/** The deterministic fallback: the page's main region. */
function focusFallback() {
  const main = document.querySelector('main')
  if (!(main instanceof HTMLElement)) return
  if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1')
  main.focus()
}

interface Session {
  key: number
  prefill: LogActivityPrefill
  contacts: SalesContact[] | Promise<SalesContact[]>
}

export function LogActivityHost({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const focusReturn = useRef<FocusReturn | null>(null)
  const [closedCount, setClosedCount] = useState(0)
  const listeners = useRef(new Set<(created: WireActivity) => void>())
  const nextKey = useRef(0)

  const open = useCallback((prefill: LogActivityPrefill, back: FocusReturn) => {
    focusReturn.current = back
    // A failed read leaves the picker empty; the pre-filled contact still shows.
    const contacts = prefill.contacts ?? fetchContacts({ limit: CONTACTS_CAP }).catch(() => [] as SalesContact[])
    nextKey.current += 1
    setSession({ key: nextKey.current, prefill, contacts })
  }, [])

  const subscribe = useCallback((fn: (created: WireActivity) => void) => {
    listeners.current.add(fn)
    return () => { listeners.current.delete(fn) }
  }, [])

  const [api] = useState<LogActivityApi>(() => ({ open, subscribe }))

  const close = useCallback(() => {
    setSession(null)
    setClosedCount(n => n + 1)
  }, [])

  // After the sheet has unmounted (its focus trap has already tried its own
  // opener), send focus where the opener asked. Runs once per close.
  useEffect(() => {
    if (closedCount === 0) return
    const back = focusReturn.current
    focusReturn.current = null
    const el = back ? back() : null
    if (el && el.isConnected) el.focus()
    else focusFallback()
  }, [closedCount])

  const emit = (created: WireActivity) => { for (const fn of listeners.current) fn(created) }

  // Slice B's handler, moved verbatim in behaviour: the activity EXISTS once
  // the create answers 2xx; a failed complete leaves it pending beside a real
  // error; a refused create keeps the sheet open with the rep's input.
  async function save(body: CreateActivityBody, markDone: boolean, typeLabel: string): Promise<boolean> {
    let created: WireActivity
    try {
      created = await createActivity(body)
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to log activity'))
      return false
    }
    if (markDone) {
      try {
        created = await completeActivity(created.id)
      } catch (e) {
        emit(created)
        toast.error(apiErrorMessage(e, MARK_DONE_FAILED))
        return true
      }
    }
    emit(created)
    toast.success(`${typeLabel} logged successfully`)
    return true
  }

  return (
    <LogActivityContext.Provider value={api}>
      {children}
      {session && (
        <LogActivitySheet key={session.key}
          onClose={close}
          onSave={save}
          contacts={session.contacts}
          initialContact={session.prefill.contact ?? null}
          initialDeal={session.prefill.deal ?? null}
        />
      )}
    </LogActivityContext.Provider>
  )
}
